import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, RefreshCw, Search, Save, X, CheckCircle, AlertTriangle } from 'lucide-react';
import MainService from '../../services/MainService';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** @param {'name'|'emirate'} mode */
const buildGeocodeQuery = (name, emirateName, mode = 'name') => {
  const locName = String(name || '').trim();
  const emi = String(emirateName || '').trim();
  if (mode === 'emirate') {
    // Emirate search: location + emirate + UAE
    return [locName, emi, 'United Arab Emirates'].filter(Boolean).join(', ');
  }
  // Name search: location name as entered (worldwide, no UAE filter)
  return locName;
};

/** @param {string} q @param {{ uaeOnly?: boolean }} opts */
const fetchNominatim = async (q, { uaeOnly = false } = {}) => {
  let url =
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}` +
    '&format=json&limit=1&addressdetails=0';
  // UAE-only only when searching with emirate
  if (uaeOnly) {
    url += '&countrycodes=ae';
  }
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'en,ar',
    },
  });
  if (!resp.ok) throw new Error('Geocode request failed');
  const data = await resp.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  const lat = Number(data[0].lat);
  const lng = Number(data[0].lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    lat: lat.toFixed(6),
    lng: lng.toFixed(6),
    displayName: data[0].display_name || q,
  };
};

/** @param {'name'|'emirate'} mode */
const geocodeLocation = async (name, emirateName, mode = 'name') => {
  const q = buildGeocodeQuery(name, emirateName, mode);
  if (!q) return null;

  const uaeOnly = mode === 'emirate';
  let result = await fetchNominatim(q, { uaeOnly });
  if (result) return result;

  // Fallback for "ANDORRA - AD - أندورا" style names (name mode only)
  if (mode === 'name') {
    const firstPart = String(name || '').split(/\s*-\s*/)[0]?.trim();
    if (firstPart && firstPart.toLowerCase() !== q.toLowerCase()) {
      result = await fetchNominatim(firstPart, { uaeOnly: false });
      if (result) return result;
    }
  }

  return null;
};

/**
 * Modal: list locations without LAT/LNG, resolve via Nominatim, save to API.
 * Find modes: by location name only, or by name + emirate name.
 */
const MapCoordinatesModal = ({ open, onClose, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const cancelledRef = useRef(false);

  const mapApiRow = (x) => ({
    id: x.Id,
    name: x.Name || '',
    nameAr: x.NameAr || '',
    emirateName: x.Emirate?.Name || '',
    emirateId: x.EmirateId || x.Emirate?.Id || '',
    areaName: x.Area?.Name || '',
    areaId: x.AreaId || x.Area?.Id || '',
    locationUrl: x.LocationUrl || '',
    lat: '',
    lng: '',
    displayName: '',
    status: 'pending', // pending | finding | found | not_found | saving | saved | error
    message: '',
  });

  const loadMissing = useCallback(async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const data = await MainService.LocationsWithoutCoordinates();
      const list = Array.isArray(data) ? data.map(mapApiRow) : [];
      setRows(list);
      setInfo(list.length === 0 ? 'All active locations already have coordinates.' : `${list.length} location(s) missing coordinates.`);
    } catch (err) {
      console.error(err);
      setError('Failed to load locations without coordinates');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    cancelledRef.current = false;
    loadMissing();
    return () => {
      cancelledRef.current = true;
    };
  }, [open, loadMissing]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const findOne = async (row, mode = 'name') => {
    updateRow(row.id, { status: 'finding', message: mode === 'emirate' ? 'By emirate…' : 'By name…' });
    try {
      const result = await geocodeLocation(row.name, row.emirateName, mode);
      if (cancelledRef.current) return;
      if (!result) {
        updateRow(row.id, { status: 'not_found', message: 'No match', lat: '', lng: '', displayName: '' });
        return;
      }
      updateRow(row.id, {
        status: 'found',
        lat: result.lat,
        lng: result.lng,
        displayName: result.displayName,
        message: mode === 'emirate' ? 'Found (emirate)' : 'Found (name)',
      });
    } catch (err) {
      if (cancelledRef.current) return;
      console.error(err);
      updateRow(row.id, { status: 'error', message: 'Lookup failed' });
    }
  };

  const handleFindOne = async (row, mode = 'name') => {
    if (busy) return;
    setBusy(true);
    await findOne(row, mode);
    setBusy(false);
  };

  const handleFindAll = async (mode = 'name') => {
    if (busy || rows.length === 0) return;
    const snapshot = rows.slice();
    const label = mode === 'emirate' ? 'name + emirate' : 'name only';
    setBusy(true);
    setError('');
    setInfo(`Looking up by ${label} for ${snapshot.length} location(s)…`);
    for (let i = 0; i < snapshot.length; i += 1) {
      if (cancelledRef.current) break;
      await findOne(snapshot[i], mode);
      // Nominatim fair-use: ~1 request / second
      if (i < snapshot.length - 1) await sleep(1100);
    }
    if (!cancelledRef.current) setInfo(`Lookup finished (${label}). Review results, then Save.`);
    setBusy(false);
  };

  const savePayload = (list) =>
    list.map((r) => ({
      Id: r.id,
      LAT: r.lat,
      LNG: r.lng,
      LocationUrl: r.displayName || r.locationUrl || '',
    }));

  const handleSaveOne = async (row) => {
    if (!row.lat || !row.lng) {
      updateRow(row.id, { status: 'error', message: 'Missing coordinates' });
      return;
    }
    updateRow(row.id, { status: 'saving', message: '' });
    try {
      const resp = await MainService.UpdateLocationCoordinates(savePayload([row]));
      if (resp === 'Success' || resp === true || resp === 'true') {
        updateRow(row.id, { status: 'saved', message: 'Saved' });
        if (onSaved) onSaved();
      } else {
        updateRow(row.id, { status: 'error', message: resp || 'Save failed' });
      }
    } catch (err) {
      console.error(err);
      updateRow(row.id, { status: 'error', message: 'Save failed' });
    }
  };

  const handleSaveAll = async () => {
    const ready = rows.filter((r) => r.lat && r.lng && r.status !== 'saved');
    if (ready.length === 0) {
      setError('No resolved coordinates to save. Run Find first.');
      return;
    }
    setBusy(true);
    setError('');
    ready.forEach((r) => updateRow(r.id, { status: 'saving' }));
    try {
      const resp = await MainService.UpdateLocationCoordinates(savePayload(ready));
      if (resp === 'Success' || resp === true || resp === 'true') {
        ready.forEach((r) => updateRow(r.id, { status: 'saved', message: 'Saved' }));
        setInfo(`${ready.length} location(s) updated.`);
        if (onSaved) onSaved();
        // Refresh remaining missing
        await loadMissing();
      } else {
        setError(typeof resp === 'string' ? resp : 'Failed to save coordinates');
        ready.forEach((r) => updateRow(r.id, { status: 'found', message: 'Save failed' }));
      }
    } catch (err) {
      console.error(err);
      setError('Failed to save coordinates');
      ready.forEach((r) => updateRow(r.id, { status: 'found', message: 'Save failed' }));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    cancelledRef.current = true;
    onClose();
  };

  if (!open) return null;

  const foundCount = rows.filter((r) => r.lat && r.lng).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="bg-[#f0f9f4] border-b border-green-100 px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center">
            <MapPin className="w-5 h-5 text-primary mr-2" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Map Coordinates</h3>
              <p className="text-xs text-gray-500">
                Find with Name = worldwide · Find with Emirate Name = UAE only
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-white/80"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={loadMissing}
            disabled={loading || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => handleFindAll('name')}
            disabled={loading || busy || rows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary text-white hover:bg-green-600 disabled:opacity-50"
            title="Search using location name only (no emirate)"
          >
            <Search className="w-4 h-4" />
            Find with Name
          </button>
          <button
            type="button"
            onClick={() => handleFindAll('emirate')}
            disabled={loading || busy || rows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50"
            title="Search using location name + emirate name"
          >
            <Search className="w-4 h-4" />
            Find with Emirate Name
          </button>
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={loading || busy || foundCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save All ({foundCount})
          </button>
          {info && <span className="text-sm text-gray-600 ml-auto">{info}</span>}
        </div>

        {error && (
          <div className="mx-6 mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="px-6 py-4 overflow-auto flex-1">
          {loading ? (
            <div className="py-12 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-gray-500 flex flex-col items-center gap-2">
              <CheckCircle className="w-8 h-8 text-primary" />
              No locations missing coordinates.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Emirate</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latitude</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Longitude</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row, index) => (
                  <tr key={row.id} className={row.status === 'saved' ? 'bg-green-50' : row.status === 'found' ? 'bg-blue-50/40' : ''}>
                    <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{row.name}</div>
                      {row.displayName ? (
                        <div className="text-xs text-gray-500 truncate max-w-[220px]" title={row.displayName}>
                          {row.displayName}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{row.emirateName || '—'}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.lat}
                        onChange={(e) => updateRow(row.id, { lat: e.target.value, status: e.target.value && row.lng ? 'found' : row.status })}
                        className="w-28 px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-primary"
                        placeholder="LAT"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.lng}
                        onChange={(e) => updateRow(row.id, { lng: e.target.value, status: row.lat && e.target.value ? 'found' : row.status })}
                        className="w-28 px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-primary"
                        placeholder="LNG"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 capitalize">
                      {row.status === 'finding' || row.status === 'saving' ? (
                        <span className="inline-flex items-center gap-1">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          {row.status}
                        </span>
                      ) : (
                        row.message || row.status.replace('_', ' ')
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-center gap-1">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleFindOne(row, 'name')}
                          className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-50"
                          title={`Find with name: ${buildGeocodeQuery(row.name, row.emirateName, 'name')}`}
                        >
                          Name
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleFindOne(row, 'emirate')}
                          className="px-2 py-1 text-xs text-teal-800 bg-teal-50 hover:bg-teal-100 rounded-lg disabled:opacity-50"
                          title={`Find with emirate: ${buildGeocodeQuery(row.name, row.emirateName, 'emirate')}`}
                        >
                          Emirate
                        </button>
                        <button
                          type="button"
                          disabled={busy || !row.lat || !row.lng || row.status === 'saved'}
                          onClick={() => handleSaveOne(row)}
                          className="p-1.5 text-primary hover:bg-green-50 rounded-lg disabled:opacity-50"
                          title="Save coordinates"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapCoordinatesModal;
